// CSRF token from the fastapi-csrf-protect cookie, plus the HTMX wiring that
// sends it on every state-changing request and fills hidden form inputs.
// Cookie format: itsdangerous URLSafeTimedSerializer — payload.timestamp.signature

export function getCsrfToken() {
  const cookie = document.cookie.split("; ").find((row) => row.startsWith("fastapi-csrf-token="));
  if (!cookie) return null;
  const cookieValue = decodeURIComponent(cookie.split("=")[1]);
  const payload = cookieValue.split(".")[0];
  const base64 = payload.replaceAll("-", "+").replaceAll("_", "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  try {
    return JSON.parse(atob(padded));
  } catch (_e) {
    return null;
  }
}

// Auto-inject X-CSRF-Token header for HTMX state-changing requests
document.body.addEventListener("htmx:configRequest", (evt) => {
  const token = getCsrfToken();
  if (token && ["POST", "PUT", "DELETE", "PATCH"].indexOf(evt.detail.verb.toUpperCase()) !== -1) {
    evt.detail.headers["X-CSRF-Token"] = token;
  }
});

// HTMX 1.x does not swap on 4xx by default. Several routes return
// a re-rendered partial with an inline error so the user keeps
// context. Opt those responses into the normal swap pipeline,
// scoped narrowly so unrelated error responses still surface as
// errors:
//   - 409 from /activate (persona activation: provisioning_error)
//   - 422 from /findings/ (manual create + link: parse / repo
//     mismatch / not-found / already-linked)
//   - 400 / 409 from a page type's /merge or /separate (the merge
//     panel re-rendered with the reason the merge was refused).
//     A 404 is deliberately left out: the route answers it with an
//     error page rather than the panel (this page type or its target
//     is gone, so the panel would be stale), and swapping that page
//     into the panel slot would bury the failure inside the detail page.
const PAGE_TYPE_MERGE_PATH = /\/archetypes\/[^/]+\/(merge|separate)$/;

document.body.addEventListener("htmx:beforeSwap", (evt) => {
  const xhr = evt.detail.xhr;
  if (!xhr) return;
  const requestPath = evt.detail.pathInfo?.requestPath || "";
  const contentType = (xhr.getResponseHeader("Content-Type") || "").toLowerCase();
  if (contentType.indexOf("text/html") !== 0) return;

  const swap409 = xhr.status === 409 && requestPath.indexOf("/activate") !== -1;
  const swap422 = xhr.status === 422 && requestPath.indexOf("/findings/") !== -1;
  const swapMerge =
    (xhr.status === 400 || xhr.status === 409) && PAGE_TYPE_MERGE_PATH.test(requestPath);
  if (swap409 || swap422 || swapMerge) {
    evt.detail.shouldSwap = true;
    evt.detail.isError = false;
  }
});

// Populate hidden CSRF inputs on page load
document.querySelectorAll('input[name="fastapi-csrf-token"]').forEach((input) => {
  if (!input.value) {
    input.value = getCsrfToken() || "";
  }
});

// Re-populate after HTMX settles (afterSettle fires after DOM is
// stable for all swap modes including outerHTML, where afterSwap's
// evt.detail.target may reference a detached element).
document.body.addEventListener("htmx:afterSettle", () => {
  document.querySelectorAll('input[name="fastapi-csrf-token"]').forEach((input) => {
    if (!input.value) {
      input.value = getCsrfToken() || "";
    }
  });
});
